// Libraries
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

// Style
import {ModalOverlay, ModalWrapper, Modal, ModalHeader, ModalBody, ModalFooter} from 'Components/message-box/index.style';

// Hooks
import Portal from 'Hooks/Portal';

const KEY_ENTER = 13;
const KEY_ESCAPE = 27;

const PATH = 'Src/components/components/message-box/index.js';

interface IMessageBox {
    readonly id?: string,
    readonly isOpen: boolean,
    readonly message?: string,
    readonly title?: string,
    readonly top?: number | string,
    readonly width?: number | string,
    readonly height?: number | string,
    children?: Element,
    onClose: (payload: {isOpen?: boolean}) => void,
    icon?: string,
    btnConfirmName?: string,
    btnCloseName?: string,
    onError?: (error: Error, detail: Record<string, unknown>) => void,
    onConfirm?: (payload: {isOpen?: boolean, id?: string}) => void
}

const MessageBox = (props: React.PropsWithChildren<IMessageBox>) => {
    const modalRef: any = useRef(null);
  
    const [isOpen, setIsOpen] = useState(props.isOpen);

    useEffect(() => {
        try {
            setIsOpen(props.isOpen);
        } catch (e) {
            if (typeof props.onError === 'function') {
                props.onError(e, {
                    component: PATH,
                    action: 'useEffect -> setIsOpen',
                    args: {}
                });
            }
        }
    }, [props.isOpen]);

    useEffect(() => {
        window.document.addEventListener('click', handleClickOutside);
        return () => {
            window.document.removeEventListener('click', handleClickOutside);
        };
    });

    const handleClickOutside = (event) => {
        try {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                handleModalPress(event);
            }
        } catch (e) {
            if (typeof props.onError === 'function') {
                props.onError(e, {
                    component: PATH,
                    action: 'handleClickOutside',
                    args: {event}
                });
            }
        }
    };

    const handleModalPress = (e: KeyboardEvent) => {
        try {
            switch (e.keyCode) {
                case KEY_ENTER:
                    if (typeof props.onConfirm !== 'undefined' && props.icon === 'icon-warning' && props.btnConfirmName) {
                        // have confirm button
                        e.preventDefault();

                        onConfirm();
                    } else if (typeof props.onClose === 'function') {
                        // only have close/cancel button
                        e.preventDefault();

                        onClose();
                    }
                    return;
                case KEY_ESCAPE:
                    if (typeof props.onClose === 'function') {
                        e.preventDefault();

                        onClose();
                    }
                    return;
                default:
                    if (typeof props.onClose === 'function') {
                        e.preventDefault();

                        onClose();
                    }
                    return;
            }

        } catch (e) {
            if (typeof props.onError === 'function') {
                props.onError(e, {
                    component: 'MessageBox',
                    action: 'handleModalPress',
                    args: {e}
                });
            }
        }
    };

    const onClose = () => {
        try {
            props.onClose({
                isOpen: false
            });

        } catch (e) {
            if (typeof props.onError === 'function') {
                props.onError(e, {
                    component: 'MessageBox',
                    action: 'onClose',
                    args: {}
                });
            }
        }
    };

    const onConfirm = () => {
        try {
            if (typeof props.onConfirm === 'function') {
                props.onConfirm({
                    isOpen: false,
                    id: props.id
                });
            }
        } catch (e) {
            if (typeof props.onError === 'function') {
                props.onError(e, {
                    component: 'MessageBox',
                    action: 'onConfirm',
                    args: {}
                });
            }
        }
    };
    
    try {
        return (
            isOpen ? (
                <Portal>
                    <ModalOverlay />
                    <ModalWrapper aria-modal={true} aria-hidden={true} tabIndex={-1} role="dialog">
                        <Modal top={props.top} width={props.width} height={props.height} ref={modalRef}>
                            <ModalHeader>
                                <h3>{props.title}</h3>
                            </ModalHeader>
                            <ModalBody>
                                <p className="mb-0">{props.message}</p>
                                {props.children}
                            </ModalBody>
                            <ModalFooter>
                                <button type="button" className="btn btn-default ml-0" onClick={onClose}>
                                    {props.btnCloseName}
                                </button>
                                {
                                    typeof onConfirm !== 'undefined' && props.btnConfirmName && (
                                        <button type="button" className="btn btn-default ml-0 mr-10" onClick={onConfirm}>
                                            {props.btnConfirmName}
                                        </button>
                                    )
                                }
                            </ModalFooter>
                        </Modal>
                    </ModalWrapper>
                </Portal>
            ) : null
        );
    } catch (e) {
        if (typeof props.onError === 'function') {
            props.onError(e, {
                component: PATH,
                action: 'render',
                args: {}
            });
        }
    }
};

const propTypes = {
    id: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    btnConfirmName: PropTypes.string,
    btnCloseName: PropTypes.string,
    onConfirm: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    onError: PropTypes.func
};

const defaultProps = {
    id: '',
    width: 370,
    height: 400,
    top: 30,
    icon: 'icon-success-green', // icon-success-green, icon-warning, icon-error-red
    isOpen: false,
    title: '',
    message: '',
    btnConfirmName: 'Confirm',
    btnCloseName: 'Cancel'
};

MessageBox.defaultProps = defaultProps;
MessageBox.propTypes = propTypes;

export default MessageBox;
